import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, uploadUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
    ChevronLeft, RefreshCw, Search, Grid, List, EyeOff, Eye, Trash2,
    Link2, Edit, Download, FileImage, Users2, Loader2, Archive, AlertTriangle,
    Sparkles, CheckSquare, Square,
} from "lucide-react";

/**
 * PhotoLibrary
 *
 * Windows-Explorer style admin view over every file in Object Storage.
 * Left pane = folder tree (All / Canonical / Orphans / Missing pointers /
 * Hidden / Backups / Fuzzy). Right pane = thumbnail grid or list. Bottom =
 * selected file details + actions. Multi-select via Ctrl/Cmd+Click and
 * Shift+Click for bulk hide / bulk link.
 *
 * Since Emergent Object Storage doesn't support DELETE, "delete" writes a
 * soft-hide marker in the `hidden_photos` Mongo collection. See PRD iter 40.
 */
function fmtBytes(n) {
    if (!n) return "0";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

// Safely render backend error payloads (string, Pydantic array, or object).
function fmtErr(d) {
    if (!d) return "Something went wrong";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
    return d?.msg || String(d);
}

const CATEGORIES = [
    { id: "all", label: "All photos", icon: FileImage },
    { id: "canonical", label: "Canonical (Name_Phone)", icon: Users2 },
    { id: "unknown", label: "Un-linked / other names", icon: AlertTriangle },
    { id: "orphans", label: "Country-code (12-digit)", icon: Archive },
    { id: "fuzzy", label: "Fuzzy name suggestions", icon: Sparkles },
    { id: "backups", label: "Backups (_backup/)", icon: Archive },
    { id: "hidden", label: "Soft-deleted", icon: EyeOff },
];

const CANONICAL_RE = /^[A-Za-z]+_\d{10}(_s)?\.(jpg|jpeg|png)$/i;
const CC_RE = /^[A-Za-z]+_\d{11,15}(_s)?\.(jpg|jpeg|png)$/i;

export default function PhotoLibrary() {
    const [files, setFiles] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [cat, setCat] = useState("all");
    const [q, setQ] = useState("");
    const [view, setView] = useState("grid");
    const [selected, setSelected] = useState(null);
    const [busy, setBusy] = useState(false);
    // Multi-select: Set of filenames, plus a `lastClicked` anchor for Shift-range.
    const [multi, setMulti] = useState(() => new Set());
    const [lastClicked, setLastClicked] = useState(null);
    // Fuzzy suggestions (loaded lazily when the "fuzzy" tab is opened)
    const [fuzzy, setFuzzy] = useState({ loading: false, items: [], loaded: false });

    const load = async () => {
        setRefreshing(true);
        try {
            const { data } = await api.get("/admin/photos/browse", {
                params: { include_orphans: true, include_backups: true, include_hidden: true },
            });
            setFiles(data.files || []);
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || "Failed to load storage");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const loadMembers = async () => {
        try {
            const { data } = await api.get("/super/districts");
            const flat = [];
            (data || []).forEach(d => (d.years || []).forEach(y => {
                (y.members || []).forEach(m => flat.push({
                    id: m.id, name: m.name, phone: m.phone,
                    year: y.year, district: d.display_name,
                }));
            }));
            setMembers(flat);
        } catch (e) {
            // Link-picker degrades gracefully — the search-then-pick flow just
            // shows "No matching members" when this fetch fails. Log for
            // debugging visibility (Sentry / browser console) without
            // interrupting the admin.
            console.warn("loadMembers failed — member picker will show empty:", e);
        }
    };

    const loadFuzzy = async () => {
        if (fuzzy.loaded) return;
        setFuzzy(f => ({ ...f, loading: true }));
        try {
            const { data } = await api.get("/admin/photos/fuzzy-suggest", { params: { min_ratio: 80, limit: 200 } });
            setFuzzy({ loading: false, items: data.suggestions || [], loaded: true });
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || "Fuzzy scan failed");
            setFuzzy({ loading: false, items: [], loaded: true });
        }
    };

    // Mount-only fetches. `load` / `loadMembers` don't close over stateful
    // deps we need to react to — they always fetch fresh from the server —
    // so an exhaustive-deps list would only re-fire them on every render.
    useEffect(() => { load(); loadMembers(); }, []);
    // Load fuzzy suggestions when the tab flips to `fuzzy`. `loadFuzzy`
    // itself is idempotent (guards on `fuzzy.loaded`) so we can safely
    // omit it from the deps.
    useEffect(() => { if (cat === "fuzzy") loadFuzzy(); }, [cat]);

    const filtered = useMemo(() => {
        if (cat === "fuzzy") return []; // fuzzy uses its own list render
        let list = files;
        if (cat === "canonical") list = list.filter(f => CANONICAL_RE.test(f.filename) && !f.is_backup && !f.is_hidden);
        else if (cat === "orphans") list = list.filter(f => f.is_cc_orphan && !f.is_backup);
        else if (cat === "backups") list = list.filter(f => f.is_backup);
        else if (cat === "hidden") list = list.filter(f => f.is_hidden);
        else if (cat === "unknown") list = list.filter(f => !CANONICAL_RE.test(f.filename) && !CC_RE.test(f.filename) && !f.is_backup && !f.is_hidden);
        else list = list.filter(f => !f.is_backup); // "all" hides raw backups
        if (q.trim()) {
            const needle = q.trim().toLowerCase();
            list = list.filter(f => f.filename.toLowerCase().includes(needle));
        }
        return list;
    }, [files, cat, q]);

    const counts = useMemo(() => ({
        all: files.filter(f => !f.is_backup).length,
        canonical: files.filter(f => CANONICAL_RE.test(f.filename) && !f.is_backup && !f.is_hidden).length,
        unknown: files.filter(f => !CANONICAL_RE.test(f.filename) && !CC_RE.test(f.filename) && !f.is_backup && !f.is_hidden).length,
        orphans: files.filter(f => f.is_cc_orphan && !f.is_backup).length,
        fuzzy: fuzzy.loaded ? fuzzy.items.length : null,
        backups: files.filter(f => f.is_backup).length,
        hidden: files.filter(f => f.is_hidden).length,
    }), [files, fuzzy]);

    // Reset multi-select whenever the visible filter changes so orphaned
    // selections from a different tab don't sneak into bulk actions.
    useEffect(() => { setMulti(new Set()); setLastClicked(null); }, [cat, q]);

    const handleTileClick = (e, f, index) => {
        // Ctrl/Cmd → toggle; Shift → range from lastClicked; else single-select
        if (e.ctrlKey || e.metaKey) {
            const s = new Set(multi);
            if (s.has(f.filename)) s.delete(f.filename); else s.add(f.filename);
            setMulti(s);
            setLastClicked(index);
            return;
        }
        if (e.shiftKey && lastClicked != null) {
            const [lo, hi] = [Math.min(lastClicked, index), Math.max(lastClicked, index)];
            const s = new Set(multi);
            for (let i = lo; i <= hi; i++) {
                const fn = filtered[i]?.filename;
                if (fn) s.add(fn);
            }
            setMulti(s);
            return;
        }
        // Plain click — clear multi, open single-select panel
        setMulti(new Set());
        setLastClicked(index);
        setSelected(f);
    };

    const clearMulti = () => { setMulti(new Set()); setLastClicked(null); };

    const bulkHide = async () => {
        const filenames = Array.from(multi);
        if (filenames.length === 0) return;
        if (!window.confirm(`Soft-delete ${filenames.length} file(s)? They stay in storage but the app treats them as gone.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/photos/hide-bulk", { filenames });
            toast.success(`${data.hidden_count} hidden`, { description: `Detached from ${data.detached_from_members} member row(s)` });
            clearMulti();
            setSelected(null);
            await load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail) || "Bulk hide failed"); }
        finally { setBusy(false); }
    };

    const bulkLink = async () => {
        const filenames = Array.from(multi);
        if (filenames.length === 0) return;
        const target = await pickMember(`Link ALL ${filenames.length} selected files to which member?`);
        if (!target) return;
        setBusy(true);
        try {
            let ok = 0;
            for (const fn of filenames) {
                try {
                    await api.post("/admin/photos/link", { filename: fn, person_id: target.id, is_spouse: target.isSpouse });
                    ok += 1;
                } catch (e) {
                    // Per-file failure — keep processing the rest; the final
                    // toast reports "ok/total" so the admin sees the shortfall.
                    console.warn("bulk link failed for", fn, e);
                }
            }
            toast.success(`Linked ${ok}/${filenames.length} to ${target.name}${target.isSpouse ? " (spouse)" : ""}`);
            clearMulti();
            await load();
        } finally { setBusy(false); }
    };

    // Shared member picker — returns { id, name, isSpouse } or null.
    const pickMember = async (headline) => {
        const searchStr = window.prompt(`${headline}\n\nType part of the name or phone:`, "");
        if (!searchStr) return null;
        const needle = searchStr.toLowerCase();
        const hits = members.filter(m =>
            (m.name || "").toLowerCase().includes(needle) ||
            (m.phone || "").includes(searchStr)
        ).slice(0, 10);
        if (hits.length === 0) { toast.error("No matching members"); return null; }
        const list = hits.map((m, i) => `${i + 1}. ${m.name} · ${m.phone} · ${m.year}`).join("\n");
        const pick = window.prompt(`Pick a member (1-${hits.length}):\n\n${list}\n\nEnter number, or 'S<n>' to link as SPOUSE photo`, "1");
        if (!pick) return null;
        const isSpouse = pick.trim().toUpperCase().startsWith("S");
        const num = parseInt(pick.replace(/[^\d]/g, ""), 10);
        const target = hits[num - 1];
        if (!target) { toast.error("Invalid choice"); return null; }
        return { id: target.id, name: target.name, isSpouse };
    };

    const hidePhoto = async (fn) => {
        if (!window.confirm(`Soft-delete "${fn}"? The bytes stay in storage but the app treats it as gone. You can restore it from the "Soft-deleted" folder.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/photos/hide", { filename: fn });
            toast.success("Photo hidden", { description: `Detached from ${data.detached_from_members} member row(s)` });
            setSelected(null);
            await load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail) || "Hide failed"); }
        finally { setBusy(false); }
    };

    const restorePhoto = async (fn) => {
        setBusy(true);
        try {
            await api.post("/admin/photos/restore", { filename: fn });
            toast.success("Restored");
            await load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail) || "Restore failed"); }
        finally { setBusy(false); }
    };

    const renamePhoto = async (fn) => {
        const nn = window.prompt("Rename file to:", fn);
        if (!nn || nn === fn) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/photos/rename", { old_filename: fn, new_filename: nn });
            toast.success("Renamed", { description: `${data.rewired_members} member pointer(s) updated. Old file soft-hidden.` });
            setSelected(null);
            await load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail) || "Rename failed"); }
        finally { setBusy(false); }
    };

    const linkToMember = async (fn) => {
        const target = await pickMember(`Link "${fn}" to a member`);
        if (!target) return;
        setBusy(true);
        try {
            await api.post("/admin/photos/link", { filename: fn, person_id: target.id, is_spouse: target.isSpouse });
            toast.success(`Linked to ${target.name}${target.isSpouse ? " (spouse)" : ""}`);
            await load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail) || "Link failed"); }
        finally { setBusy(false); }
    };

    // Approve a fuzzy suggestion — links the file to the pre-matched person.
    const approveFuzzy = async (s, isSpouse = false) => {
        setBusy(true);
        try {
            await api.post("/admin/photos/link", { filename: s.filename, person_id: s.person_id, is_spouse: isSpouse });
            toast.success(`Linked to ${s.person_name}${isSpouse ? " (spouse)" : ""}`);
            setFuzzy(f => ({ ...f, items: f.items.filter(x => x.filename !== s.filename) }));
            await load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail) || "Approve failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-rotary-cream">
            <header className="border-b border-rotary-gold/20 bg-white sticky top-0 z-20">
                <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Link to="/admin/photos" data-testid="library-back-link">
                            <Button variant="ghost" size="sm"><ChevronLeft className="h-4 w-4 mr-1" />Photo dashboard</Button>
                        </Link>
                        <h1 className="text-lg font-semibold text-rotary-blue">Photo library</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search filename…"
                                className="pl-8 h-9 w-64"
                                data-testid="library-search"
                            />
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setView(view === "grid" ? "list" : "grid")} data-testid="library-view-toggle">
                            {view === "grid" ? <List className="h-4 w-4" /> : <Grid className="h-4 w-4" />}
                        </Button>
                        <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="library-refresh">
                            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                        </Button>
                    </div>
                </div>
                {/* Bulk-action bar — only when >0 files are checkbox-selected */}
                {multi.size > 0 && (
                    <div className="border-t border-rotary-blue/20 bg-rotary-blue/5 px-6 py-2 flex items-center justify-between text-sm" data-testid="library-bulk-bar">
                        <div className="flex items-center gap-2 text-rotary-blue font-medium">
                            <CheckSquare className="h-4 w-4" />
                            {multi.size} selected · Ctrl/Cmd+Click toggles · Shift+Click ranges
                        </div>
                        <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={bulkLink} disabled={busy} data-testid="library-bulk-link">
                                <Link2 className="h-3.5 w-3.5 mr-1" /> Link all to member
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={bulkHide} disabled={busy} data-testid="library-bulk-hide">
                                <Trash2 className="h-3.5 w-3.5 mr-1" /> Hide selected
                            </Button>
                            <Button size="sm" variant="ghost" onClick={clearMulti} data-testid="library-bulk-clear">Clear</Button>
                        </div>
                    </div>
                )}
            </header>

            <div className="max-w-[1400px] mx-auto px-6 py-4 flex gap-4">
                {/* Left folder tree */}
                <aside className="w-56 shrink-0">
                    <div className="rounded-lg border border-slate-200 bg-white p-2 sticky top-20">
                        {CATEGORIES.map(c => {
                            const IconC = c.icon;
                            const count = counts[c.id];
                            const active = cat === c.id;
                            return (
                                <button
                                    key={c.id}
                                    onClick={() => setCat(c.id)}
                                    className={`w-full flex items-center justify-between rounded-md px-2 py-1.5 text-sm text-left ${active ? "bg-rotary-blue text-white" : "hover:bg-slate-100 text-slate-700"}`}
                                    data-testid={`library-cat-${c.id}`}
                                >
                                    <span className="flex items-center gap-2">
                                        <IconC className="h-3.5 w-3.5" />
                                        <span className="truncate">{c.label}</span>
                                    </span>
                                    <span className={`text-xs ${active ? "opacity-80" : "text-slate-500"}`}>
                                        {count == null ? "—" : count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                {/* Main grid */}
                <div className="flex-1 min-w-0">
                    {cat === "fuzzy" ? (
                        <FuzzyPanel state={fuzzy} onApprove={approveFuzzy} onReload={() => { setFuzzy({ loading: false, items: [], loaded: false }); loadFuzzy(); }} busy={busy} />
                    ) : loading ? (
                        <div className="flex justify-center items-center h-64">
                            <Loader2 className="h-6 w-6 animate-spin text-rotary-blue" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <Card className="p-10 text-center text-slate-500" data-testid="library-empty">
                            <FileImage className="h-6 w-6 mx-auto mb-2" />
                            No files in this folder.
                        </Card>
                    ) : view === "grid" ? (
                        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                            {filtered.map((f, i) => {
                                const isChecked = multi.has(f.filename);
                                return (
                                    <button
                                        key={f.filename}
                                        onClick={(e) => handleTileClick(e, f, i)}
                                        className={`group relative rounded-lg border overflow-hidden bg-white hover:shadow-md transition ${isChecked ? "ring-2 ring-rotary-gold" : selected?.filename === f.filename ? "ring-2 ring-rotary-blue" : "border-slate-200"} ${f.is_hidden ? "opacity-50" : ""}`}
                                        data-testid={`library-tile-${f.filename}`}
                                    >
                                        <div className="aspect-square bg-slate-100 relative">
                                            <img
                                                src={uploadUrl(f.filename)}
                                                alt={f.filename}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                                            />
                                            {isChecked && <div className="absolute top-1 left-1 bg-rotary-gold text-white rounded p-0.5"><CheckSquare className="h-3 w-3" /></div>}
                                            {f.is_hidden && <div className="absolute top-1 right-1 bg-red-600 text-white text-[9px] px-1.5 py-0.5 rounded">HIDDEN</div>}
                                            {f.is_cc_orphan && !f.is_hidden && <div className="absolute top-1 right-1 bg-amber-500 text-white text-[9px] px-1.5 py-0.5 rounded">12-DIGIT</div>}
                                            {f.is_backup && <div className="absolute top-1 right-1 bg-slate-600 text-white text-[9px] px-1.5 py-0.5 rounded">BACKUP</div>}
                                        </div>
                                        <div className="p-1.5 text-left">
                                            <div className="text-[11px] font-mono truncate text-slate-700">{f.filename.replace(/^_backup\//, "")}</div>
                                            <div className="text-[10px] text-slate-500">{fmtBytes(f.size)}</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                                    <tr>
                                        <th className="w-8 px-3 py-2"></th>
                                        <th className="text-left px-3 py-2">Filename</th>
                                        <th className="text-left px-3 py-2 w-24">Size</th>
                                        <th className="text-left px-3 py-2 w-40">Modified</th>
                                        <th className="text-left px-3 py-2 w-24">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((f, i) => {
                                        const isChecked = multi.has(f.filename);
                                        return (
                                            <tr key={f.filename}
                                                onClick={(e) => handleTileClick(e, f, i)}
                                                className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${isChecked ? "bg-rotary-gold/10" : selected?.filename === f.filename ? "bg-rotary-blue/5" : ""}`}
                                                data-testid={`library-row-${f.filename}`}>
                                                <td className="px-3 py-2 text-slate-400">
                                                    {isChecked ? <CheckSquare className="h-4 w-4 text-rotary-gold" /> : <Square className="h-4 w-4" />}
                                                </td>
                                                <td className="px-3 py-2 font-mono text-xs">{f.filename}</td>
                                                <td className="px-3 py-2 text-slate-500">{fmtBytes(f.size)}</td>
                                                <td className="px-3 py-2 text-slate-500 text-xs">{f.last_modified || "—"}</td>
                                                <td className="px-3 py-2 text-xs">
                                                    {f.is_hidden && <span className="text-red-600">Hidden</span>}
                                                    {f.is_cc_orphan && !f.is_hidden && <span className="text-amber-600">12-digit</span>}
                                                    {f.is_backup && <span className="text-slate-500">Backup</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Selected file panel — hidden while multi-selecting to avoid confusion */}
                {selected && multi.size === 0 && cat !== "fuzzy" && (
                    <aside className="w-72 shrink-0">
                        <div className="rounded-lg border border-slate-200 bg-white p-3 sticky top-20" data-testid="library-selected-panel">
                            <div className="aspect-square bg-slate-100 rounded overflow-hidden mb-3">
                                <img src={uploadUrl(selected.filename)} alt={selected.filename} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            </div>
                            <div className="text-xs font-mono break-all mb-1 text-slate-800">{selected.filename}</div>
                            <div className="text-[11px] text-slate-500 space-y-0.5 mb-3">
                                <div>Size: {fmtBytes(selected.size)}</div>
                                <div>Modified: {selected.last_modified || "—"}</div>
                                {selected.is_hidden && <div className="text-red-600">Soft-deleted</div>}
                                {selected.is_cc_orphan && !selected.is_hidden && <div className="text-amber-600">Country-code prefixed orphan</div>}
                                {selected.is_backup && <div className="text-slate-600">Rename backup snapshot</div>}
                            </div>
                            <div className="grid gap-1.5">
                                <a href={uploadUrl(selected.filename)} download target="_blank" rel="noopener noreferrer">
                                    <Button size="sm" variant="outline" className="w-full justify-start" data-testid="library-download">
                                        <Download className="h-3.5 w-3.5 mr-1.5" /> Open / Download
                                    </Button>
                                </a>
                                {!selected.is_hidden && (
                                    <>
                                        <Button size="sm" variant="outline" onClick={() => linkToMember(selected.filename)} disabled={busy} className="justify-start" data-testid="library-link">
                                            <Link2 className="h-3.5 w-3.5 mr-1.5" /> Link to member
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => renamePhoto(selected.filename)} disabled={busy} className="justify-start" data-testid="library-rename">
                                            <Edit className="h-3.5 w-3.5 mr-1.5" /> Rename
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => hidePhoto(selected.filename)} disabled={busy} className="justify-start text-red-600 hover:bg-red-50" data-testid="library-hide">
                                            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete (soft)
                                        </Button>
                                    </>
                                )}
                                {selected.is_hidden && (
                                    <Button size="sm" onClick={() => restorePhoto(selected.filename)} disabled={busy} className="justify-start" data-testid="library-restore">
                                        <Eye className="h-3.5 w-3.5 mr-1.5" /> Restore
                                    </Button>
                                )}
                            </div>
                            <div className="mt-3 pt-3 border-t border-slate-100 text-[10px] text-slate-400 leading-relaxed">
                                Tip: Ctrl / ⌘ + click tiles to multi-select; Shift + click for range. Then use the bar up top to hide or link many at once.
                            </div>
                        </div>
                    </aside>
                )}
            </div>
        </div>
    );
}

/**
 * Fuzzy suggestions panel — one row per (photo → guessed member) pair with
 * approval buttons. Backed by GET /admin/photos/fuzzy-suggest.
 */
function FuzzyPanel({ state, onApprove, onReload, busy }) {
    if (state.loading && !state.loaded) {
        return (
            <div className="flex justify-center items-center h-64" data-testid="library-fuzzy-loading">
                <Loader2 className="h-6 w-6 animate-spin text-rotary-blue" />
            </div>
        );
    }
    if (state.items.length === 0) {
        return (
            <Card className="p-10 text-center text-slate-500 space-y-3" data-testid="library-fuzzy-empty">
                <Sparkles className="h-6 w-6 mx-auto text-slate-400" />
                <div>No fuzzy suggestions right now.</div>
                <div className="text-xs text-slate-400">Files matching by phone are handled automatically. This tab surfaces filenames that carry a NAME close to a member (≥80% similarity).</div>
                <Button size="sm" variant="outline" onClick={onReload} data-testid="library-fuzzy-reload">
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Re-scan
                </Button>
            </Card>
        );
    }
    return (
        <div className="space-y-2" data-testid="library-fuzzy-list">
            <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-slate-600">
                    {state.items.length} suggestion(s) — approve to link, high-confidence first.
                </div>
                <Button size="sm" variant="outline" onClick={onReload} data-testid="library-fuzzy-reload">
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Re-scan
                </Button>
            </div>
            {state.items.map(s => (
                <Card key={s.filename} className="p-3 flex items-center gap-3 border border-slate-200" data-testid={`library-fuzzy-row-${s.filename}`}>
                    <div className="w-16 h-16 shrink-0 bg-slate-100 rounded overflow-hidden">
                        <img src={uploadUrl(s.filename)} alt={s.filename} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono truncate text-slate-800">{s.filename}</div>
                        <div className="text-xs text-slate-600 mt-0.5">
                            Suggested match: <span className="font-medium">{s.person_name}</span> · {s.person_phone}
                        </div>
                        <div className="text-[11px] text-emerald-600 mt-0.5">{s.match_ratio}% similarity</div>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                        <Button size="sm" onClick={() => onApprove(s, false)} disabled={busy} data-testid={`library-fuzzy-approve-${s.filename}`}>
                            <Link2 className="h-3.5 w-3.5 mr-1" /> Link as photo
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => onApprove(s, true)} disabled={busy} data-testid={`library-fuzzy-approve-spouse-${s.filename}`}>
                            Link as spouse
                        </Button>
                    </div>
                </Card>
            ))}
        </div>
    );
}
